import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const UserMobileCard = ({
  user,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      manager: 'bg-orange-100 text-orange-800 border-orange-200',
      viewer: 'bg-green-100 text-green-800 border-green-200'
    };

    const roleLabels = {
      admin: 'Admin',
      manager: 'Manager',
      viewer: 'Viewer'
    };

    return (
      <span className={cn(
        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border',
        roleStyles?.[role] || 'bg-gray-100 text-gray-800 border-gray-200'
      )}>
        {roleLabels?.[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={cn(
        'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border',
        statusStyles?.[status] || 'bg-gray-100 text-gray-800 border-gray-200'
      )}>
        <div className={cn(
          'w-1.5 h-1.5 rounded-full mr-1',
          status === 'active' ? 'bg-green-500' : 'bg-gray-500'
        )}></div>
        {status === 'active' ? 'Active' : 'Inactive'}
      </span>
    );
  };

  return (
    <div className={cn(
      'bg-white rounded-lg border border-border shadow-sm transition-colors',
      isSelected && 'border-blue-300 bg-blue-50'
    )}>
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelect?.(user?.id, checked)}
              aria-label={`Select ${user?.name}`}
              className="mt-1"
            />
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <div className="flex-shrink-0 h-8 w-8">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-foreground truncate">
                    {user?.name}
                  </h3>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-2 truncate">
                {user?.email}
              </p>
              
              <div className="flex items-center space-x-2">
                {getRoleBadge(user?.role)}
                {getStatusBadge(user?.status)}
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            className="ml-2 flex-shrink-0"
          />
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-muted/20">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Last Login
              </label>
              <p className="text-sm text-foreground mt-1">
                {formatDate(user?.lastLogin)}
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Created
              </label>
              <p className="text-sm text-foreground mt-1">
                {formatDate(user?.createdAt)}
              </p>
            </div>

            {/* Role Description */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Permissions
              </label>
              <p className="text-sm text-foreground mt-1">
                {user?.role === 'admin' && 'Full system access and user management'}
                {user?.role === 'manager' && 'Member management and reporting access'}
                {user?.role === 'viewer' && 'Read-only access to member data'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-2 mt-4 pt-3 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit?.(user)}
              iconName="Edit"
              iconPosition="left"
            >
              Edit
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleStatus?.(user)}
              iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
              iconPosition="left"
              className={cn(
                user?.status === 'active' ?'border-orange-300 text-orange-700 hover:bg-orange-50' :'border-green-300 text-green-700 hover:bg-green-50'
              )}
            >
              {user?.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
            
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete?.(user)}
              iconName="Trash2"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMobileCard;